import { useState } from "react";
import { AlertCircle, Check, Circle, Lock, Minus, Pencil, Settings2, Sparkles } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Progress } from "@/shared/ui/progress";
import { cn } from "@/shared/lib/utils";

import type { Checklist, ChecklistItem, FieldSpec } from "../../api/types";
import {
  COMING_SOON_GROUP_TEXT,
  conflictCandidates,
  fieldValueText,
  groupProgress,
  isComingSoonGroup,
  isInternalSource,
  legendCounts,
  NEWS_SYNC_TEXT,
  rowLabel,
  rowState,
  sourceLabel,
  specPath,
  STATUS_LABEL,
  type StatusTone,
} from "../../model/collect";
import { rowEditable, type ImageUploadTarget } from "../../model/rowEdit";
import { ChecklistRowEditor } from "./ChecklistRowEditor";

/** 行内编辑的接线(0925 B7);不给就是只读清单。 */
export interface ChecklistEditing {
  /** 保存一行(调用方发 answers);回 true 收起编辑框。 */
  onSave: (path: string, value: unknown) => Promise<boolean>;
  /** 图片行上传(A.53):裸 base64 → 完整 url。 */
  upload: (target: ImageUploadTarget, base64: string) => Promise<string>;
  /** host 模式才有地方放图。 */
  hostMode: boolean;
  /** 别的写口在路上时禁用(串行提交,A.36)。 */
  disabled: boolean;
}

/**
 * 屏 3 右栏「资料清单对账」:{done} / {total} 徽章(spec 级,直接读 api 的 progress,附录 A.22)+ 进度条 +
 * 图例三格 + 分组行(mark / label / 值或状态 / 来源 chip / 事实字段锁)+ 事实字段说明块。
 *
 * ★ 徽章、图例、分组计数**同一个口径**:spec 级(三款产品的「产品名」算一项)、只算可采集字段。徽章直接读 api;
 *   图例和分组从各项状态按 spec 字段现数(「已预填」格 = 徽章分子),总数不重算。缺失但模板有默认值的项画
 *   「默认:…」(A.25),excluded 画「不公开」(A.21),creative 画「AI 起草」、news_feed 画「自动同步」(A.33)——
 *   都不进图例、不进分组进度;全组都不可采集时组标题不画计数(不画一个 0/0)。
 * ★ `warnings`(只有预填 / 答题回包会带,A.33)在页头下画成橙色「预填提示」列表;GET 回来是空的就不画。
 * ★ 冲突行不画一个「值」:全部候选连同各自来源列出来(api 把排第一的候选放在 `value`、其余放 `conflicts`,两处合起来才全,
 *   见 `conflictCandidates`),选哪个是左边那道题的事。
 * ★ repeat 实例行的名字补「 · N」(api 只给模板 label,三款产品都叫「产品名」,见 `rowLabel`)。
 * ★ 来源是内部资料(api 在 sourceLabel 末尾加「(内部资料)」,附录 A.26)的,来源旁挂橙色「确认后才公开」;
 *   已确认的项不挂(确认过就是要公开)。
 * ★ 0925 B7:给了 `editing` 时,每一行可采集的项(17 项里的)带「编辑」—— 行内改完即 `user:row:{path}` 确认,
 *   不依赖追问流;同一时刻只开一行。AI 起草 / 默认 / 自动同步 / 不公开的行不给改(见 `rowEditable`)。
 */
export function ChecklistPanel({
  checklist,
  fields,
  editing,
}: {
  checklist: Checklist;
  /** 模板 schema 按 spec 路径索引(拿 `default`,附录 A.36);拉不到模板给 null,页面照样能画。 */
  fields: ReadonlyMap<string, FieldSpec> | null;
  editing?: ChecklistEditing;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const { done, total } = checklist.progress;
  const warnings = (checklist.warnings ?? []).filter((w) => w.trim() !== "");
  const hasDefault = (p: string) => fields?.get(p)?.default != null;
  const legend = legendCounts(checklist.groups, hasDefault);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <aside
      aria-labelledby="website-checklist-title"
      className={cn("flex flex-col gap-4 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 id="website-checklist-title" className="text-sm font-semibold text-foreground">
          资料清单对账
        </h2>
        <Badge variant="secondary" className="normal-case tabular-nums tracking-normal">
          {done} / {total}
        </Badge>
      </div>
      {warnings.length > 0 && (
        <section
          aria-labelledby="website-checklist-warnings"
          className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-2xs text-warning"
        >
          <h3 id="website-checklist-warnings" className="font-semibold">
            预填提示
          </h3>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 leading-relaxed">
            {warnings.map((w, i) => (
              <li key={`${i}-${w}`}>{w}</li>
            ))}
          </ul>
        </section>
      )}
      <Progress value={pct} aria-label="采集进度" className="h-2" />
      <div role="group" aria-label="图例" className="grid grid-cols-3 gap-2 text-2xs">
        <LegendCell tone="ok" label="已预填" n={legend.done} />
        <LegendCell tone="warn" label="待确认" n={legend.conflict} />
        <LegendCell tone="missing" label="缺失" n={legend.missing} />
      </div>
      <p className="-mt-2 text-2xs text-muted-foreground">
        按字段计:同一项在几款产品 / 几个案例里只算一次;AI
        起草、默认、自动同步、不公开的不计(整项都选了不公开算已答)。
      </p>

      <div className="space-y-4">
        {checklist.groups.map((g) => {
          if (isComingSoonGroup(g)) {
            return (
              <section key={g.group} aria-label={g.group} className="space-y-1.5">
                <h3 className="text-xs font-semibold text-foreground">{g.group}</h3>
                <p className="text-2xs text-muted-foreground">{COMING_SOON_GROUP_TEXT}</p>
              </section>
            );
          }
          const p = groupProgress(g, hasDefault);
          return (
            <section key={g.group} aria-label={g.group} className="space-y-1.5">
              <h3 className="text-xs font-semibold text-foreground">
                {p.total > 0 ? `${g.group} ${p.done}/${p.total}` : g.group}
              </h3>
              <ul className="space-y-1">
                {g.items.map((item) => (
                  <Row
                    key={item.path}
                    item={item}
                    field={fields?.get(specPath(item.path))}
                    editing={editing}
                    open={open === item.path}
                    onOpen={() => setOpen(item.path)}
                    onClose={() => setOpen(null)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <p className="border-t border-border/60 pt-3 text-2xs leading-relaxed text-muted-foreground">
        电话、地址、资质、价格是
        <span className="mx-0.5 inline-flex items-center gap-0.5 font-semibold text-foreground">
          <Lock className="h-3 w-3" aria-hidden />
          事实字段
        </span>
        :只能从资料里取或你手填,AI 不会替你编。
      </p>
    </aside>
  );
}

/**
 * 图例一格。★ 裸 `div` 上的 `aria-label` 读屏不认(generic 角色不许命名):整格按一张「图」给名字
 * (`role="img"` + `aria-label="已预填 12"`),里面的图标和字只是它的画面。
 */
function LegendCell({ tone, label, n }: { tone: StatusTone; label: string; n: number }) {
  return (
    <div
      role="img"
      aria-label={`${label} ${n}`}
      className="flex items-center justify-between rounded-md border border-border/70 px-2 py-1"
    >
      <span className="flex items-center gap-1 text-muted-foreground">
        <Mark tone={tone} />
        {label}
      </span>
      <strong className="tabular-nums text-foreground">{n}</strong>
    </div>
  );
}

const MARK_CLASS: Record<StatusTone, string> = {
  ok: "bg-success/15 text-success",
  warn: "bg-warning/15 text-warning",
  missing: "border border-dashed border-muted-foreground/60 text-muted-foreground",
  default: "bg-muted text-muted-foreground",
  excluded: "bg-muted text-muted-foreground/70",
  creative: "bg-muted text-muted-foreground",
};

function Mark({ tone, label }: { tone: StatusTone; label?: string }) {
  const Icon =
    tone === "ok"
      ? Check
      : tone === "warn"
        ? AlertCircle
        : tone === "default"
          ? Settings2
          : tone === "excluded"
            ? Minus
            : tone === "creative"
              ? Sparkles
              : Circle;
  return (
    <span
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
      className={cn("grid h-4 w-4 shrink-0 place-items-center rounded-full", MARK_CLASS[tone])}
    >
      <Icon className={cn("h-2.5 w-2.5", tone === "missing" && "opacity-0")} />
    </span>
  );
}

const TEXT_CLASS: Record<StatusTone, string> = {
  ok: "text-foreground",
  warn: "font-medium text-warning",
  missing: "text-muted-foreground",
  default: "text-muted-foreground",
  excluded: "text-muted-foreground",
  creative: "text-muted-foreground",
};

/** 附录 A.26 的橙色提示:内部资料抽出来的值,用户确认前不上站。 */
function InternalNote() {
  return (
    <span className="rounded bg-warning/10 px-1 py-px text-2xs font-medium text-warning">
      确认后才公开
    </span>
  );
}

function Row({
  item,
  field,
  editing,
  open,
  onOpen,
  onClose,
}: {
  item: ChecklistItem;
  field: FieldSpec | undefined;
  editing?: ChecklistEditing;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const state = rowState(item, field);
  const editable = editing != null && rowEditable(item, field);
  const showSource = state.tone === "ok";
  const internal = item.status !== "confirmed" && isInternalSource(item.sourceLabel);
  const type = item.type ?? field?.type;
  const markLabel = type === "news_feed" ? NEWS_SYNC_TEXT : STATUS_LABEL[item.status];
  const candidates = item.status === "conflict" ? conflictCandidates(item) : [];
  return (
    <li className="border-b border-border/50 py-1 text-xs last:border-b-0">
      <div className="flex items-start gap-2">
        <span className="mt-0.5">
          <Mark tone={state.tone} label={markLabel} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="flex items-center gap-1 text-foreground">
              {rowLabel(item)}
              {item.fact && (
                <span className="inline-flex items-center gap-0.5 text-2xs text-muted-foreground">
                  <Lock className="h-3 w-3" aria-hidden />
                  事实字段
                </span>
              )}
              {state.tone === "creative" && (
                <span className="rounded bg-muted px-1 py-px text-2xs text-muted-foreground">
                  {STATUS_LABEL.creative}
                </span>
              )}
            </span>
            <span className="flex min-w-0 items-center gap-1">
              {state.text && (
                <span className={cn("min-w-0 truncate text-right", TEXT_CLASS[state.tone])}>
                  {state.text}
                </span>
              )}
              {editable && !open && (
                <button
                  type="button"
                  aria-label={`编辑:${rowLabel(item)}`}
                  title="直接改这一项"
                  disabled={editing.disabled}
                  onClick={onOpen}
                  className="grid h-5 w-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                >
                  <Pencil className="h-3 w-3" aria-hidden />
                </button>
              )}
            </span>
          </div>
          {showSource && (
            <div className="flex flex-wrap items-center justify-end gap-x-1.5 text-2xs text-muted-foreground">
              {internal && <InternalNote />}
              <span>{sourceLabel(item)}</span>
            </div>
          )}
          {candidates.length > 0 && (
            <div className="mt-0.5 space-y-0.5">
              {candidates.map((c, i) => (
                // 候选值可能是结构值、也可能两条同值不同出处:按下标当 key;值走 fieldValueText(枚举码翻人话)。
                <div key={i} className="flex flex-wrap justify-between gap-x-2 text-2xs">
                  <span className="text-foreground">{fieldValueText(c.value, type)}</span>
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    {isInternalSource(c.sourceLabel) && <InternalNote />}
                    <span>{c.sourceLabel}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {editable && open && (
            <ChecklistRowEditor
              item={item}
              field={field}
              hostMode={editing.hostMode}
              disabled={editing.disabled}
              upload={editing.upload}
              onCancel={onClose}
              onSave={async (value) => {
                const ok = await editing.onSave(item.path, value);
                if (ok) onClose();
                return ok;
              }}
            />
          )}
          {item.status === "excluded" && item.value != null && item.value !== "" && (
            <div className="text-2xs text-muted-foreground/70 line-through">
              {fieldValueText(item.value, type)}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
